import React, { useState, useEffect } from 'react';
import { Building, Clock, CheckCircle, AlertCircle, Target, Lightbulb, Plus, X, AlertTriangle, Sparkles, MessageSquare, Wand2 } from 'lucide-react';
import { getSafeToken } from '../../../../../utils/tokenHelper';
import ExampleTemplates from '../../../../../components/ExampleTemplates';
import useExampleTemplates from '../../../../../hooks/useExampleTemplates';

// Silenciar logs de EPL por defecto (no afecta a Vertex).
// Activar explícitamente con: VITE_EPL_VERBOSE=true
const _eplConsole = globalThis.console;
// eslint-disable-next-line no-shadow
const console = import.meta.env.VITE_EPL_VERBOSE === 'true'
    ? _eplConsole
    : { ..._eplConsole, log: () => {}, info: () => {}, warn: () => {} };

// Configuración de API Base - Mismo patrón que innovatechService.js
const getApiBaseUrl = () => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  
  if (window.location.hostname.includes('edutechpulselab.com')) {
    return 'https://edutechpulselab.com/api';
  }
  
  return 'http://localhost:3001/api';
};

const API_BASE_URL = getApiBaseUrl();

const BMLBuildStep = ({ onValidateIA, initialData = null, isActive = false }) => {
    const [formData, setFormData] = useState({
        mvp_descripcion: '',
        mvp_features: [{
            funcion: '',
            implementacion_simple: '',
            criterio_aprendizaje: ''
        }],
        tiempo_numero: '',
        tiempo_unidad: 'semanas'
    });
    
    const [validation, setValidation] = useState({
        mvp_descripcion: { valid: false, message: '' },
        mvp_features: { valid: false, message: '' },
        tiempo_estimado: { valid: false, message: '' }
    });

    // IA Assistance States
    const [microFeedback, setMicroFeedback] = useState(null);
    const [mvpSuggestions, setMvpSuggestions] = useState([]);
    const [featureSuggestions, setFeatureSuggestions] = useState([]);
    const [isLoadingIA, setIsLoadingIA] = useState(false);
    const [lastAnalyzedText, setLastAnalyzedText] = useState('');

    // Example Templates System
    const { examples: mvpExamples } = useExampleTemplates('lab1', 'bml_build');
    const { examples: funcionesExamples } = useExampleTemplates('lab1', 'mvp_funciones');

    // Prueba directa de carga
    const [directTestData, setDirectTestData] = useState(null);
    useEffect(() => {
        import('../../../../../services/innovatech/exampleTemplates.json').then(data => {
            console.log('🧪 [Direct Test] Raw imported data:', data);
            const testData = data.default?.labs?.lab1?.mvp_funciones;
            console.log('🧪 [Direct Test] Functions data:', testData);
            setDirectTestData(testData);
        }).catch(err => {
            console.error('🧪 [Direct Test] Import failed:', err);
        });
    }, []);

    // Debug logs
    console.log('🔍 [Debug] mvpExamples:', mvpExamples);
    console.log('🔍 [Debug] funcionesExamples:', funcionesExamples);
    console.log('🔍 [Debug] directTestData:', directTestData);

    // 💾 Cargar datos iniciales si existen
    useEffect(() => {
        if (initialData && typeof initialData === 'object') {
            console.log('[💾 BMLBuildPhase] Cargando datos iniciales:', initialData);
            setFormData(prevData => ({
                ...prevData,
                ...initialData
            }));
        }
    }, [initialData]);

    // Validación en tiempo real
    useEffect(() => {
        const tiempoNumeroValido = formData.tiempo_numero && !isNaN(formData.tiempo_numero) && parseFloat(formData.tiempo_numero) > 0;
        
        setValidation({
            mvp_descripcion: {
                valid: formData.mvp_descripcion.length >= 120,
                message: formData.mvp_descripcion.length >= 120 
                    ? 'Longitud mínima alcanzada ✓' 
                    : `Mínimo 120 caracteres (actual: ${formData.mvp_descripcion.length})`
            },
            mvp_features: {
                valid: formData.mvp_features.length > 0 && formData.mvp_features[0].funcion.length >= 8,
                message: formData.mvp_features.length > 0 && formData.mvp_features[0].funcion.length >= 8
                    ? 'Features definidas correctamente'
                    : 'Define al menos 1 característica (mín. 8 caracteres en función)'
            },
            tiempo_estimado: {
                valid: tiempoNumeroValido,
                message: tiempoNumeroValido
                    ? `Tiempo estimado: ${formData.tiempo_numero} ${formData.tiempo_unidad}`
                    : 'Especifica un tiempo válido en número'
            }
        });
    }, [formData]);

    const isFormComplete = Object.values(validation).every(v => v.valid);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleFeatureChange = (index, field, value) => {
        const newFeatures = [...formData.mvp_features];
        newFeatures[index] = {
            ...newFeatures[index],
            [field]: value
        };
        setFormData(prev => ({
            ...prev,
            mvp_features: newFeatures
        }));
    };

    const addFeature = () => {
        if (formData.mvp_features.length < 3) {
            setFormData(prev => ({
                ...prev,
                mvp_features: [...prev.mvp_features, {
                    funcion: '',
                    implementacion_simple: '',
                    criterio_aprendizaje: ''
                }]
            }));
        }
    };

    const removeFeature = (index) => {
        if (formData.mvp_features.length > 1) {
            const newFeatures = formData.mvp_features.filter((_, i) => i !== index);
            setFormData(prev => ({
                ...prev,
                mvp_features: newFeatures
            }));
        }
    };

    const handleValidateIA = () => {
        if (isFormComplete) {
            const tiempoCompleto = `${formData.tiempo_numero} ${formData.tiempo_unidad}`;
            const buildInput = `
MVP: ${formData.mvp_descripcion}

Características esenciales:
${formData.mvp_features.map((feature, i) => `
${i + 1}. Función crítica: ${feature.funcion}
   Implementación simple: ${feature.implementacion_simple}
   Criterio de aprendizaje: ${feature.criterio_aprendizaje || 'No especificado'}`).join('\n')}

Tiempo estimado de construcción: ${tiempoCompleto}
            `.trim();
            
            const enhancedFormData = {
                ...formData,
                mvp_tiempo_estimado: tiempoCompleto
            };
            
            onValidateIA(buildInput, 'bml_build', enhancedFormData);
        }
    };

    // IA Assistance Functions
    const handleMicroFeedback = async () => {
        if (formData.mvp_descripcion.length < 50) return;
        
        setIsLoadingIA(true);
        try {
            const token = getSafeToken();
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const response = await fetch(`${API_BASE_URL}/asistentes/ai/micro/mvp_descripcion`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    hypothesis: initialData?.hypothesis || '',
                    mvp_description: formData.mvp_descripcion
                })
            });
            
            if (response.ok) {
                const result = await response.json();
                if (result.success && result.data) {
                    // Detectar el origen real del feedback
                    const actualSource = result.data._source || result.meta?.source || 'unknown';
                    
                    if (actualSource === 'simulation') {
                        console.log('🎭 [SIMULACIÓN] Micro-feedback de simulación inteligente:', result.data);
                    } else {
                        console.log('🤖 [IA REAL] Micro-feedback recibido desde OpenAI:', result.data);
                    }
                    
                    setMicroFeedback({
                        ...result.data,
                        _source: actualSource // Usar el origen real
                    });
                    setLastAnalyzedText(formData.mvp_descripcion); // Registrar texto analizado
                } else {
                    console.warn('[⚠️ MICRO-FEEDBACK] Respuesta sin datos:', result);
                }
            } else {
                console.error('[❌ MICRO-FEEDBACK] Error HTTP:', response.status, response.statusText);
            }
        } catch (error) {
            console.error('❌ [ERROR API] Micro-feedback falló, usando fallback local:', error);
            // Fallback to local feedback
            setMicroFeedback({
                fortaleza: "El MVP está bien acotado en alcance y tiempo",
                mejora_sugerida: "Especificar qué métrica concreta validará el éxito",
                pregunta_reflexiva: "¿Cómo sabrás si los usuarios realmente valoran esta solución?",
                score_claridad: 75,
                accion_inmediata: "Definir 1-2 métricas de comportamiento clave",
                _source: 'fallback' // Marcador de origen local
            });
            setLastAnalyzedText(formData.mvp_descripcion); // Registrar texto analizado
        } finally {
            setIsLoadingIA(false);
        }
    };

    const handleMvpSuggestions = async () => {
        setIsLoadingIA(true);
        try {
            const token = getSafeToken();
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const response = await fetch(`${API_BASE_URL}/asistentes/ai/suggest/mvp`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    hypothesis: initialData?.hypothesis || '',
                    problem_context: formData.mvp_descripcion
                })
            });
            
            if (response.ok) {
                const result = await response.json();
                if (result.success && result.data) {
                    // Detectar origen real de las sugerencias
                    const actualSource = result.data[0]?._source || result.meta?.source || 'unknown';
                    
                    if (actualSource === 'simulation') {
                        console.log('🎭 [SIMULACIÓN] Sugerencias MVP de templates locales:', result.data);
                    } else {
                        console.log('🤖 [IA REAL] Sugerencias MVP recibidas desde OpenAI:', result.data);
                    }
                    
                    setMvpSuggestions(result.data); // Ya incluyen _source desde backend
                } else {
                    console.warn('[⚠️ MVP-SUGGEST] Respuesta sin datos:', result);
                }
            } else {
                console.error('[❌ MVP-SUGGEST] Error HTTP:', response.status, response.statusText);
            }
        } catch (error) {
            console.error('❌ [ERROR API] MVP suggestions falló, usando fallback local:', error);
            // Fallback suggestions
            setMvpSuggestions([
                {
                    titulo: "Landing Page con Formulario",
                    descripcion: "Página simple que explique la propuesta de valor y capture interés con formulario de contacto",
                    tiempo_estimado: "3-5 días",
                    validacion_clave: "Nivel de interés real del segmento objetivo",
                    _source: 'fallback'
                },
                {
                    titulo: "Prototipo Figma Interactivo",
                    descripcion: "Mockup navegable que simule las funciones principales sin desarrollo backend",
                    tiempo_estimado: "1-2 semanas",
                    validacion_clave: "Usabilidad y comprensión del flujo principal",
                    _source: 'fallback'
                }
            ]);
        } finally {
            setIsLoadingIA(false);
        }
    };

    const handleFeatureSuggestions = async () => {
        setIsLoadingIA(true);
        try {
            const token = getSafeToken();
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const response = await fetch(`${API_BASE_URL}/asistentes/ai/suggest/features`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    hypothesis: initialData?.hypothesis || '',
                    current_mvp: formData.mvp_descripcion
                })
            });
            
            if (response.ok) {
                const result = await response.json();
                if (result.success && result.data) {
                    setFeatureSuggestions(result.data);
                } else {
                    console.warn('[⚠️ FEATURES-SUGGEST] Respuesta sin datos:', result);
                }
            } else {
                console.error('[❌ FEATURES-SUGGEST] Error HTTP:', response.status, response.statusText);
            }
        } catch (error) {
            console.error('Error fetching feature suggestions:', error);
            // Fallback suggestions
            setFeatureSuggestions([
                {
                    funcion: "Registro simple con email",
                    prioridad: "alta",
                    razonamiento: "Captura leads y permite seguimiento de usuarios interesados",
                    complejidad: "simple"
                },
                {
                    funcion: "Formulario de onboarding básico",
                    prioridad: "alta", 
                    razonamiento: "Comprende perfil del usuario y personaliza experiencia inicial",
                    complejidad: "simple"
                }
            ]);
        } finally {
            setIsLoadingIA(false);
        }
    };

    // DISABLED: Auto micro-feedback (generic responses don't add value)
    // useEffect(() => {
    //     const description = formData.mvp_descripcion;
    //     const milestones = [120, 200, 300];
    //     const currentMilestone = milestones.find(m => 
    //         description.length === m || 
    //         (description.length > m && description.length <= m + 5)
    //     );
        
    //     if (currentMilestone && !microFeedback) {
    //         const timeoutId = setTimeout(() => {
    //             handleMicroFeedback();
    //         }, 500);
            
    //         return () => clearTimeout(timeoutId);
    //     }
    // }, [formData.mvp_descripcion, microFeedback]);

    if (!isActive) return null;

    return (
        <div className="max-w-[800px] mx-auto space-y-6 px-4">
            {/* 🔸 Contexto de hipótesis (si existe) - Estilo neutro */}
            {initialData?.hypothesis && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                    <p className="text-xs text-gray-600 font-medium mb-2 uppercase tracking-wide">Tu hipótesis actual</p>
                    <p className="text-sm text-gray-700 italic">"{initialData.hypothesis}"</p>
                </div>
            )}

            {/* ✏️ Título contextual plano integrado */}
            <div className="mt-6">
                <h3 className="text-gray-900 font-semibold text-base mb-2">BUILD: Construye tu MVP</h3>
                <p className="text-gray-500 text-sm mb-6">Define qué construir para validar tu hipótesis específica</p>
            </div>

            {/* 📝 1. Descripción del MVP - Card blanca neutra */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
                <div className="border-b border-gray-200 pb-4 mb-6">
                    <h2 className="text-gray-900 font-semibold text-[15px]">1. Descripción del MVP</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Enfócate en la función mínima que valide tu hipótesis. No en funcionalidades completas.
                    </p>
                </div>
                
                <label className="block text-sm font-medium text-gray-700 mb-3">
                    Describe tu MVP mínimo *
                </label>
                
                <div className="relative">
                    <textarea
                        value={formData.mvp_descripcion}
                        onChange={(e) => handleInputChange('mvp_descripcion', e.target.value)}
                        placeholder=""
                        className="w-full min-h-[140px] p-4 border border-gray-200 rounded-lg text-sm leading-relaxed resize-vertical focus:ring-2 focus:ring-blue-300 focus:border-blue-500 transition-colors"
                    />
                    {/* Ejemplo integrado y discreto */}
                    {formData.mvp_descripcion.length === 0 && (
                        <div className="absolute inset-4 pointer-events-none text-gray-400 text-sm leading-relaxed">
                            <span className="text-gray-500 font-medium">Ejemplo:</span> Landing page donde estudiantes de ingeniería se registran para recibir notificaciones de prácticas filtradas. Si 50+ se registran en 2 semanas, valido que el filtrado les importa...
                        </div>
                    )}
                </div>
                


                {/* 🤖 Micro-feedback IA - Solo si es relevante */}
                {microFeedback && microFeedback._source === 'openai' && (
                    <div className="bg-gradient-to-r from-green-50/30 to-blue-50/30 border-l-4 border-green-400 rounded-r-lg p-4 mt-4">
                        <div className="flex items-start gap-3">
                            <div className="p-1.5 bg-green-100 rounded-lg">
                                <MessageSquare className="w-4 h-4 text-green-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-3">
                                    <h4 className="font-medium text-green-900 text-sm">Análisis IA</h4>
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded-md font-medium">
                                        🤖 IA Real
                                    </span>
                                </div>
                                
                                <div className="space-y-3 text-xs">
                                    <div>
                                        <span className="font-medium text-green-700">✓ Fortaleza: </span>
                                        <span className="text-gray-700">{microFeedback.fortaleza}</span>
                                    </div>
                                    
                                    <div>
                                        <span className="font-medium text-amber-700">⚡ Mejora: </span>
                                        <span className="text-gray-700">{microFeedback.mejora_sugerida}</span>
                                    </div>
                                    
                                    <div className="bg-white/70 border border-purple-100 rounded p-2">
                                        <span className="font-medium text-purple-700">🤔 Reflexiona: </span>
                                        <span className="text-gray-800 italic">{microFeedback.pregunta_reflexiva}</span>
                                    </div>
                                    
                                    {microFeedback.accion_inmediata && (
                                        <div>
                                            <span className="font-medium text-blue-700">→ Siguiente paso: </span>
                                            <span className="text-gray-700">{microFeedback.accion_inmediata}</span>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="flex items-center gap-2 mt-3 pt-2 border-t border-purple-100">
                                    <span className="text-xs font-medium text-gray-600">Claridad:</span>
                                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                                        <div 
                                            className={`h-2 rounded-full transition-all duration-500 ${
                                                microFeedback.score_claridad >= 80 ? 'bg-green-500' :
                                                microFeedback.score_claridad >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                            }`}
                                            style={{ width: `${microFeedback.score_claridad}%` }}
                                        ></div>
                                    </div>
                                    <span className="text-xs font-medium text-gray-700">{microFeedback.score_claridad}%</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Status mínimo y discreto */}
                <div className="flex items-center justify-between mt-2">
                    <ExampleTemplates 
                        examples={mvpExamples}
                        onSelectExample={(example) => {
                            // Función para usar ejemplo como base
                            const baseText = `Inspirado en ${example.titulo}: ${example.mvp_descripcion}`;
                            handleInputChange('mvp_descripcion', baseText);
                        }}
                    />
                    <span className={`text-xs ${
                        validation.mvp_descripcion.valid 
                            ? 'text-emerald-600' 
                            : 'text-gray-400'
                    }`}>
                        {formData.mvp_descripcion.length}/120 chars {validation.mvp_descripcion.valid ? '✓' : ''}
                    </span>
                </div>



                {/* 💡 Panel de ejemplos MVP - Diseño card limpio */}
                {mvpSuggestions.length > 0 && (
                    <div className="mt-6 bg-white border border-blue-200 rounded-xl shadow-sm overflow-hidden">
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-blue-200">
                            <div className="flex items-center gap-2">
                                <div className="p-1 bg-blue-100 rounded-lg">
                                    <Sparkles className="w-4 h-4 text-blue-600" />
                                </div>
                                <h4 className="font-semibold text-blue-900 text-sm">Ejemplos de MVP para tu hipótesis</h4>
                                {mvpSuggestions[0]?._source === 'openai' ? (
                                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">🤖 IA Real</span>
                                ) : mvpSuggestions[0]?._source === 'simulation' ? (
                                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">🎭 Simulación</span>
                                ) : null}
                            </div>
                        </div>
                        
                        <div className="p-4">
                            <div className="grid gap-3">
                                {mvpSuggestions.map((mvp, index) => (
                                    <div key={index} className="group p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm transition-all duration-200">
                                        <div className="flex items-start justify-between mb-2">
                                            <h5 className="font-medium text-gray-900 text-sm group-hover:text-blue-900 transition-colors">{mvp.titulo}</h5>
                                            <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-md border border-blue-200">
                                                {mvp.tiempo_estimado}
                                            </span>
                                        </div>
                                        <p className="text-xs text-gray-600 mb-2 leading-relaxed">{mvp.descripcion}</p>
                                        <div className="flex items-center gap-1 text-xs">
                                            <span className="text-purple-600 font-medium">Valida:</span>
                                            <span className="text-gray-700">{mvp.validacion_clave}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            <div className="mt-3 p-2.5 bg-blue-50/50 border border-blue-200/50 rounded-lg">
                                <p className="text-xs text-blue-700 flex items-start gap-2">
                                    <span className="text-blue-500">💡</span>
                                    <span><strong>Tip:</strong> Usa estos ejemplos como inspiración. Tu MVP debe ser único para tu hipótesis específica.</span>
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ⚙️ 2. Características principales - Card blanca neutra */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
                <div className="border-b border-gray-200 pb-4 mb-6">
                    <h2 className="text-gray-900 font-semibold text-[15px]">2. Características principales</h2>
                    <p className="text-sm text-gray-500 mt-1">Define las funciones mínimas del MVP. Máximo 3.</p>
                    <p className="text-xs text-gray-500 mt-2">
                        <span className="font-medium">1ra función:</span> Crítica • <span className="font-medium">2da:</span> Observación • <span className="font-medium">3ra:</span> Diferida
                    </p>
                </div>
                
                <div className="space-y-6">
                    {formData.mvp_features.map((feature, index) => (
                        <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="font-medium text-gray-900 text-sm">
                                    Característica {index + 1} – {
                                        index === 0 ? 'Función crítica (principal)' :
                                        index === 1 ? 'Función de observación (secundaria)' :
                                        'Función diferida (a validar luego)'
                                    }
                                </h4>
                                {formData.mvp_features.length > 1 && (
                                    <button
                                        onClick={() => removeFeature(index)}
                                        className="text-red-500 hover:text-red-700 p-1 rounded transition-colors"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-2 uppercase tracking-wide">
                                        {
                                            index === 0 ? 'Función crítica (principal)' :
                                            index === 1 ? 'Función de observación (secundaria)' :
                                            'Función diferida'
                                        }
                                    </label>
                                    <input
                                        type="text"
                                        value={feature.funcion}
                                        onChange={(e) => handleFeatureChange(index, 'funcion', e.target.value)}
                                        placeholder={
                                            index === 0 ? "Ej.: Filtrado por especialización (función que resuelve el problema principal)" :
                                            index === 1 ? "Ej.: Dashboard básico (función que permite observar comportamiento)" :
                                            "Ej.: Notificaciones push (función a validar después del MVP inicial)"
                                        }
                                        className="w-full p-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-300 focus:border-blue-500 transition-colors bg-white"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-2 uppercase tracking-wide">
                                        Implementación simple en el MVP
                                    </label>
                                    <input
                                        type="text"
                                        value={feature.implementacion_simple}
                                        onChange={(e) => handleFeatureChange(index, 'implementacion_simple', e.target.value)}
                                        placeholder={
                                            index === 0 ? "Ej.: Filtro manual con Excel (implementación crítica mínima)" :
                                            index === 1 ? "Ej.: Hoja Google Sheets con contador (métricas básicas)" :
                                            "Ej.: Formulario de interés simple (validar demanda futura)"
                                        }
                                        className="w-full p-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-300 focus:border-blue-500 transition-colors bg-white"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-2 uppercase tracking-wide">
                                        Qué aprenderás con esta función (opcional)
                                    </label>
                                    <input
                                        type="text"
                                        value={feature.criterio_aprendizaje}
                                        onChange={(e) => handleFeatureChange(index, 'criterio_aprendizaje', e.target.value)}
                                        placeholder="Ej.: ¿Realmente les importa el filtrado? (tasa de registro)"
                                        className="w-full p-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-300 focus:border-blue-500 transition-colors bg-white"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                    
                    {formData.mvp_features.length < 3 && (
                        <button
                            onClick={addFeature}
                            className="w-full p-4 border-2 border-dashed border-gray-300 text-gray-600 font-medium rounded-lg hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
                        >
                            <Plus className="w-4 h-4" />
                            + Agregar característica esencial ({formData.mvp_features.length}/3)
                        </button>
                    )}

                    {/* Ejemplos de Funciones */}
                    <div className="mt-2">
                        {/* Debug info temporal */}
                        <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                            <strong>Debug:</strong> funcionesExamples = {funcionesExamples ? 'LOADED' : 'NULL'} | 
                            Type: {typeof funcionesExamples} | 
                            Has examples: {funcionesExamples?.examples ? 'YES' : 'NO'} |
                            Count: {funcionesExamples?.examples?.length || 0}
                        </div>

                        <ExampleTemplates 
                            examples={funcionesExamples}
                            onSelectExample={(example) => {
                                // Agregar función de ejemplo en el primer campo vacío o al final
                                const lastFeatureIndex = formData.mvp_features.length - 1;
                                const currentFunction = formData.mvp_features[lastFeatureIndex]?.funcion || '';
                                
                                if (currentFunction.length === 0) {
                                    // Si el último campo está vacío, usar ese
                                    handleFeatureChange(lastFeatureIndex, 'funcion', example.ejemplo_escritura);
                                } else {
                                    // Si no, agregar nueva función si es posible
                                    if (formData.mvp_features.length < 3) {
                                        addFeature();
                                        // Usar setTimeout para asegurar que el nuevo campo se agregue
                                        setTimeout(() => {
                                            const newIndex = formData.mvp_features.length;
                                            handleFeatureChange(newIndex, 'funcion', example.ejemplo_escritura);
                                        }, 100);
                                    }
                                }
                            }}
                        />
                    </div>
                    
                    {/* Status sutil */}
                    <div className={`px-3 py-1.5 rounded-md text-xs flex items-center gap-2 ${
                        validation.mvp_features.valid 
                            ? 'bg-emerald-50/80 text-emerald-700 border border-emerald-200/50'
                            : 'bg-amber-50/80 text-amber-700 border border-amber-200/50'
                    }`}>
                        {validation.mvp_features.valid ? (
                            <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                        ) : (
                            <div className="w-1.5 h-1.5 bg-amber-500 rounded-full"></div>
                        )}
                        {validation.mvp_features.message}
                    </div>

                    {/* Guía práctica sin IA genérica */}
                    <div className="mt-4 p-3 bg-gradient-to-r from-purple-50/30 to-indigo-50/30 rounded-lg border border-purple-200/50">
                        <div className="flex items-start gap-2 text-xs">
                            <Wand2 className="w-3.5 h-3.5 text-purple-600 mt-0.5 flex-shrink-0" />
                            <div className="text-purple-800">
                                <p className="font-medium mb-1">💡 Guía para definir funciones</p>
                                <p className="text-purple-700 leading-relaxed mb-2">
                                    <strong>Función 1 (Crítica):</strong> La que resuelve directamente tu hipótesis<br/>
                                    <strong>Función 2 (Observación):</strong> Te permite medir el comportamiento<br/>
                                    <strong>Función 3 (Diferida):</strong> Mejora la experiencia pero no es esencial
                                </p>
                                <p className="text-xs text-purple-600 italic">
                                    Sugerencias IA personalizadas disponibles al completar el formulario
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Sugerencias de funciones IA */}
                    {featureSuggestions.length > 0 && (
                        <div className="mt-4 bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
                            <h4 className="font-medium text-purple-900 text-sm mb-3 flex items-center gap-2">
                                <Wand2 className="w-4 h-4" />
                                Funciones sugeridas por IA
                            </h4>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {featureSuggestions.map((feature, index) => (
                                    <div key={index} className="bg-white border border-purple-100 rounded-lg p-3">
                                        <div className="flex items-start justify-between mb-2">
                                            <h5 className="font-medium text-gray-900 text-sm">{feature.funcion}</h5>
                                            <div className="flex items-center gap-1">
                                                <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                                                    feature.prioridad === 'alta' ? 'text-red-700 bg-red-100' :
                                                    feature.prioridad === 'media' ? 'text-yellow-700 bg-yellow-100' :
                                                    'text-green-700 bg-green-100'
                                                }`}>
                                                    {feature.prioridad}
                                                </span>
                                                <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                                                    feature.complejidad === 'simple' ? 'text-blue-700 bg-blue-100' :
                                                    feature.complejidad === 'media' ? 'text-orange-700 bg-orange-100' :
                                                    'text-red-700 bg-red-100'
                                                }`}>
                                                    {feature.complejidad}
                                                </span>
                                            </div>
                                        </div>
                                        <p className="text-xs text-gray-700">{feature.razonamiento}</p>
                                        
                                        <button
                                            onClick={() => {
                                                if (formData.mvp_features.length < 3) {
                                                    addFeature();
                                                    // Pre-fill with suggested function
                                                    setTimeout(() => {
                                                        const newIndex = formData.mvp_features.length;
                                                        handleFeatureChange(newIndex, 'funcion', feature.funcion);
                                                    }, 100);
                                                }
                                            }}
                                            disabled={formData.mvp_features.length >= 3}
                                            className="mt-2 text-xs font-medium text-purple-600 hover:text-purple-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                                        >
                                            {formData.mvp_features.length >= 3 ? 'Máximo 3 funciones' : '+ Agregar esta función'}
                                        </button>
                                    </div>
                                ))}
                            </div>
                            
                            <div className="mt-3 p-2 bg-purple-100 border border-purple-200 rounded text-xs text-purple-800">
                                💡 <strong>Tip:</strong> Prioriza funciones de alta importancia y baja complejidad para tu MVP
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ⏰ 3. Tiempo estimado - Card blanca neutra */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
                <div className="border-b border-gray-200 pb-4 mb-6">
                    <h2 className="text-gray-900 font-semibold text-[15px] flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-600" />
                        3. Tiempo estimado
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">¿Cuánto tiempo necesitas para construir este MVP?</p>
                </div>
                
                <label className="block text-sm font-medium text-gray-700 mb-3">
                    Tiempo estimado de construcción *
                </label>
                
                <div className="flex flex-col sm:flex-row gap-3 p-4 border border-gray-200 rounded-lg bg-gray-50 mb-4">
                    <input
                        type="number"
                        min="0.1"
                        step="0.1"
                        value={formData.tiempo_numero}
                        onChange={(e) => handleInputChange('tiempo_numero', e.target.value)}
                        placeholder="2"
                        className="flex-1 p-2 border border-gray-200 rounded text-sm font-medium bg-white focus:ring-2 focus:ring-blue-300 focus:border-blue-500"
                    />
                    <select
                        value={formData.tiempo_unidad}
                        onChange={(e) => handleInputChange('tiempo_unidad', e.target.value)}
                        className="p-2 border border-gray-200 rounded text-sm font-medium text-gray-900 cursor-pointer bg-white focus:ring-2 focus:ring-blue-300 focus:border-blue-500"
                    >
                        <option value="horas">horas</option>
                        <option value="días">días</option>
                        <option value="semanas">semanas</option>
                        <option value="meses">meses</option>
                    </select>
                </div>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2 mb-4">
                    <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <p className="text-yellow-800 text-xs font-medium">
                        Debe poder ejecutarse en ≤ 2 semanas. Si toma más, tu MVP es muy complejo.
                    </p>
                </div>
                
                <div className={`px-3 py-1.5 rounded-md text-xs flex items-center gap-2 ${
                    validation.tiempo_estimado.valid 
                        ? 'bg-emerald-50/80 text-emerald-700 border border-emerald-200/50'
                        : 'bg-red-50/80 text-red-700 border border-red-200/50'
                }`}>
                    {validation.tiempo_estimado.valid ? (
                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                    ) : (
                        <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                    )}
                    {validation.tiempo_estimado.message}
                </div>
            </div>

            {/* � Recordatorios finales - Estilo neutro con accento pedagógico */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2 text-sm">
                    <Lightbulb className="w-4 h-4 text-blue-600" />
                    Recordatorios clave antes de enviar
                </h4>
                <div className="space-y-3">
                    <div className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <div>
                            <strong>Enfócate:</strong> Solo en validar tu hipótesis específica.
                        </div>
                    </div>
                    <div className="flex items-start gap-2 text-sm text-gray-700">
                        <AlertTriangle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div>
                            <strong>Pregúntate:</strong> ¿Este MVP me dirá si mi hipótesis es correcta?
                        </div>
                    </div>
                </div>
            </div>

            {/* 🚀 CTA Inteligente con progreso IA */}
            <div className="pt-2 space-y-3">
                {/* Indicador de progreso IA */}
                {(microFeedback || mvpSuggestions.length > 0 || featureSuggestions.length > 0) && (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium text-green-800">Asistencia IA activada</span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-green-700">
                            {microFeedback && (
                                <div className="flex items-center gap-1">
                                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                    Feedback en tiempo real
                                </div>
                            )}
                            {mvpSuggestions.length > 0 && (
                                <div className="flex items-center gap-1">
                                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                    {mvpSuggestions.length} ejemplos MVP
                                </div>
                            )}
                            {featureSuggestions.length > 0 && (
                                <div className="flex items-center gap-1">
                                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                                    {featureSuggestions.length} funciones sugeridas
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <button
                    onClick={handleValidateIA}
                    disabled={!isFormComplete}
                    className={`w-full px-6 py-4 text-base font-semibold rounded-lg flex items-center justify-center gap-2 transition-all ${
                        isFormComplete 
                            ? microFeedback?.score_claridad >= 80
                                ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-sm hover:shadow-md cursor-pointer'
                                : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md cursor-pointer'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                    title={
                        isFormComplete 
                            ? microFeedback?.score_claridad >= 80 
                                ? "MVP optimizado - Avanza a MEASURE con confianza"
                                : "Avanza a la siguiente fase: MEASURE"
                            : "Completa todos los campos para continuar"
                    }
                >
                    {isFormComplete ? (
                        <>
                            <CheckCircle className="w-5 h-5" />
                            {microFeedback?.score_claridad >= 80 
                                ? 'Validar MVP Optimizado' 
                                : 'Validar con IA'}
                        </>
                    ) : (
                        <>
                            <AlertCircle className="w-5 h-5" />
                            Completa todos los campos
                        </>
                    )}
                </button>

                {/* Progress hint */}
                {isFormComplete && !microFeedback && (
                    <div className="text-center">
                        <p className="text-xs text-gray-500">
                            💡 Prueba el feedback IA escribiendo al menos 120 caracteres en la descripción
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BMLBuildStep;